import {last} from 'lodash';
import {connect} from 'react-redux';

import {getHasAutoShareDrafts} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ResolveStateProps} from '../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  findInboxesPanelIdInPanelId,
  makeViewerPanelId,
  NewPanelId,
  PanelTypesEnum,
  RCellsPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {computeIsDraftComment} from '../../../helpers/models/draftCommentHelpers';
import {CommentModel} from '../../../models/api/commentModel';
import {ConversationHighlightModel} from '../../../models/api/conversationHighlightModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {ConversationOverlayModel} from '../../../models/api/conversationOverlayModel';
import {AppState} from '../../../store/appState';
import {makeGetApplicationChannelTypeFromConversation} from '../../../store/data/applicationChannelTypes/applicationChannelTypesSelectors';
import {makeGetVisualRecipient} from '../../../store/data/contacts/recipientsSelectors';
import {
  LastCommentDraftModel,
  makeGetAssignee,
  makeGetBumpActivity,
  makeGetBumpComment,
  makeGetBumpTeammateSource,
  makeGetCellCommentingTeammate,
  makeGetCellPrecogs,
  makeGetCellReplyingTeammate,
  makeGetCommentDraftSummaryWithAuthor,
  makeGetConversationInbox,
  makeGetConversationTags,
  makeGetDate,
  makeGetDiscussionTrackerTeammates,
  makeGetDisplayableCommentDraftSummary,
  makeGetIsStarred,
  makeGetLastComment,
  makeGetLastCommentWithAuthor,
  makeGetLastDraftSeedAuthors,
  makeGetLastManualMessage,
  makeGetNumMessages,
  makeGetReminderDate,
  makeGetShouldConsiderInPrivateTag,
  makeGetStatus,
  makeGetTeam,
  makeGetTrackerStatus,
  makeGetUnreadStatus,
  makeGetVisualSearchActivity,
} from '../../../store/data/conversations/conversationCellSelectors';
import {makeGetVisualConversationSenders} from '../../../store/data/conversations/conversationSendersSelectors';
import {
  makeGetDraftComments,
  makeGetDraftSeeds,
  makeGetLastDraftMessageSummary,
} from '../../../store/data/conversations/conversationsSelectors';
import {makeGetExternalChannelTypeFromConversation} from '../../../store/data/externalChannelTypes/externalChannelTypesSelectors';
import {makeGetInbox} from '../../../store/data/inboxes/inboxesSelectors';
import {makeGetEarliestSlaBreach} from '../../../store/data/rules/ruleBundlesSelectors';
import {getMentionableAliases} from '../../../store/data/teammates/mentionTeammatesSelectors';
import {getFindTeammate, getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {InboxesConversationsParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {findParametersViewId} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {getPanels} from '../../../store/panels/panelsSelectors';
import {ViewerPanelPageTypesEnum} from '../../../store/panels/viewer/viewerPanelState';
import {ConversationCell} from './conversationCell';
import {makeGetShouldRenderConversationSenders} from './conversationCellCommon';

/*
 * Props.
 */

export interface ConversationCellContainerProps {
  panelId: NewPanelId;
  parameters: InboxesConversationsParameters | undefined;
  cellsContext: CellsContext;
  conversation: ConversationModel;
  isExtraConversation: boolean;
  conversationOverlay?: ConversationOverlayModel;
  conversationHighlight?: ConversationHighlightModel;
  isSearch?: boolean;
}

/*
 * Selectors.
 */

type DraftCommentsSelector = AppParametricSelector<
  ConversationCellContainerProps,
  ReadonlyArray<CommentModel>
>;

function getPanelId(_state: AppState, props: ConversationCellContainerProps) {
  return props.panelId;
}

function getConversation(_state: AppState, props: ConversationCellContainerProps) {
  return props.conversation;
}

function getConversationSenders(_state: AppState, props: ConversationCellContainerProps) {
  return props.conversation.senders;
}

function getParameters(_state: AppState, props: ConversationCellContainerProps) {
  return props.parameters;
}

function getCellsContext(_state: AppState, props: ConversationCellContainerProps) {
  return props.cellsContext;
}

function getDelegatedTeammateId(_state: AppState, props: ConversationCellContainerProps) {
  return props.cellsContext.teammateId;
}

function getIsExtraConversation(_state: AppState, props: ConversationCellContainerProps) {
  return props.isExtraConversation;
}

function getConversationOverlay(_state: AppState, props: ConversationCellContainerProps) {
  return props.conversationOverlay;
}

function getSearchActivity(_state: AppState, props: ConversationCellContainerProps) {
  return props.conversationHighlight?.searchActivity;
}

function getIsSearch(_state: AppState, props: ConversationCellContainerProps) {
  return props.isSearch;
}

/*
 * Selectors for last draft comment (comment of status "draft").
 */

function makeGetLastCommentDraft(getDraftComments: DraftCommentsSelector) {
  return createSelector([getDraftComments], (draftComments) => last(draftComments));
}

function makeGetLastCommentDraftAuthor(getLastCommentDraft: ReturnType<typeof makeGetLastCommentDraft>) {
  return createSelector(
    [getLastCommentDraft, getFindTeammate],
    (lastCommentDraft, findTeammate) => lastCommentDraft && findTeammate(lastCommentDraft.authorId),
  );
}

function makeGetLastCommentDraftWithAuthor(getDraftComments: DraftCommentsSelector) {
  const getLastCommentDraft = makeGetLastCommentDraft(getDraftComments);
  return createSelector(
    [getLastCommentDraft, makeGetLastCommentDraftAuthor(getLastCommentDraft)],
    (comment, author) => {
      // TODO: Update `getDraftComments` to return `DraftCommentModel`, so we don't have to check the type here.
      if (!comment || !computeIsDraftComment(comment) || !author) {
        return undefined;
      }

      const result: LastCommentDraftModel = {comment, author};
      return result;
    },
  );
}

/*
 * Selectors for overlay.
 */

const getOverlayInboxId = createSelector([getConversationOverlay], (overlay) => overlay?.inboxId);

function makeGetOverlay() {
  const getInbox = makeGetInbox(getOverlayInboxId);
  return createSelector(
    [getUserId, getIsExtraConversation, getConversation, getFindTeammate, getInbox, getConversationOverlay],
    (userId, isExtraConversation, conversation, findTeammate, inbox, overlay) => {
      if (!isExtraConversation || !overlay) {
        return undefined;
      }

      const {type, requesterId, assigneeId} = overlay;
      return {
        type,
        requester: requesterId && requesterId !== userId ? findTeammate(requesterId) : undefined,
        assignee: assigneeId ? findTeammate(assigneeId) : undefined,
        inbox,
        conversationId: conversation.id,
      };
    },
  );
}

/*
 * Additional selectors for bump activities, bump comments, and more.
 */

function makeGetOriginalSeenAt() {
  return createSelector([getPanelId, getPanels, getConversation], (panelId, panels, conversation) => {
    if (!RCellsPanelId.is(panelId)) {
      return undefined;
    }

    const inboxesPanelId = findInboxesPanelIdInPanelId(panelId);
    const viewerPanelId = makeViewerPanelId(inboxesPanelId);
    const viewerPanel = panels[viewerPanelId];
    if (viewerPanel?.type !== PanelTypesEnum.VIEWER) {
      return undefined;
    }

    const page = viewerPanel.pages.next || viewerPanel.pages.current;
    if (
      page?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ||
      page.conversationId !== conversation.id ||
      !page.originalConversationState
    ) {
      return undefined;
    }

    // If we originally opened the conversation with unread content, allow the user to go back to that unread state.
    const {lastActivityDate, seenAt} = page.originalConversationState;
    return !seenAt || seenAt < lastActivityDate ? seenAt : undefined;
  });
}

const getConversationLastCommentDraft = createSelector(
  [getConversation],
  (conversation) => conversation.lastCommentDraft,
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationCellContainerProps>()(() => {
  const getUnreadStatus = makeGetUnreadStatus(getConversation, getCellsContext);
  const getCellPrecogs = wrapInShallowSelector(makeGetCellPrecogs(getConversation, getUnreadStatus));
  const getTrackerTeammates = wrapInShallowSelector(
    makeGetDiscussionTrackerTeammates(getConversation, getDelegatedTeammateId),
  );
  const getBumpActivity = makeGetBumpActivity(getConversation);
  const getConversationTags = wrapInShallowSelector(makeGetConversationTags(getConversation));
  const getOverlay = wrapInShallowSelector(makeGetOverlay());
  const getOriginalSeenAt = makeGetOriginalSeenAt();
  const getDraftComments = makeGetDraftComments(getConversation);
  const getDraftCommentSummary = makeGetDisplayableCommentDraftSummary(getConversationLastCommentDraft);
  const getDraftSeeds = makeGetDraftSeeds(getConversation);
  const getLastComment = makeGetLastComment(getConversation, getDraftComments);
  const getVisualConversationSenders = makeGetVisualConversationSenders(
    getConversation,
    getUserId,
    getConversationSenders,
  );

  return {
    date: makeGetDate(getConversation, getDelegatedTeammateId, getParameters, getCellsContext),
    status: makeGetStatus(getCellsContext, getConversation),
    unreadStatus: getUnreadStatus,
    trackerStatus: makeGetTrackerStatus(getDelegatedTeammateId, getConversation),
    trackerTeammates: getTrackerTeammates,
    tags: getConversationTags,
    recipient: makeGetVisualRecipient(getConversation, getDelegatedTeammateId, 'conversationCellContainer'),
    assignee: makeGetAssignee(getConversation, getParameters),
    team: makeGetTeam(getConversation, getCellsContext),
    inbox: makeGetConversationInbox(getConversation, getParameters, getCellsContext),
    lastComment: makeGetLastCommentWithAuthor(getLastComment),
    lastManualMessage: makeGetLastManualMessage(getConversation),
    commentingTeammate: makeGetCellCommentingTeammate(getCellPrecogs),
    replyingTeammate: makeGetCellReplyingTeammate(getCellPrecogs),
    reminderDate: makeGetReminderDate(getConversation, getCellsContext),
    draft: makeGetLastDraftMessageSummary(getConversation),
    lastCommentDraft: makeGetLastCommentDraftWithAuthor(getDraftComments),
    commentDraftSummary: makeGetCommentDraftSummaryWithAuthor(getDraftCommentSummary),
    overlay: getOverlay,
    mentionableAliases: getMentionableAliases,
    bumpActivity: getBumpActivity,
    bumpComment: makeGetBumpComment(getConversation, getBumpActivity, getDraftComments),
    bumpTeammateSource: makeGetBumpTeammateSource(getBumpActivity),
    draftSeedAuthors: makeGetLastDraftSeedAuthors(getDraftSeeds),
    originalSeenAt: getOriginalSeenAt,
    isStarred: makeGetIsStarred(getConversation, getDelegatedTeammateId),
    userId: getUserId,

    numMessages: makeGetNumMessages(getConversation),
    externalChannelType: makeGetExternalChannelTypeFromConversation(getConversation),
    applicationChannelType: makeGetApplicationChannelTypeFromConversation(getConversation),
    searchActivity: makeGetVisualSearchActivity(getSearchActivity),
    shouldConsiderInPrivateTag: makeGetShouldConsiderInPrivateTag(
      getConversation,
      getParameters,
      getCellsContext,
    ),
    conversationSenders: getVisualConversationSenders,
    hasAutoShareDrafts: getHasAutoShareDrafts,
    shouldRenderConversationSenders: makeGetShouldRenderConversationSenders(
      getVisualConversationSenders,
      getIsSearch,
    ),
    slaTimerDate: makeGetEarliestSlaBreach(getConversation),
  };
});

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  _dispatchProps: unknown,
  ownProps: ConversationCellContainerProps,
) {
  // Remove parameters from props since it's not stable and not used.
  const {parameters, ...remainingOwnProps} = ownProps;
  const sourceCustomViewId = parameters && findParametersViewId(parameters);

  return {
    sourceCustomViewId,
    ...remainingOwnProps,
    ...stateProps,
  };
}

/*
 * Component.
 */

export const ConversationCellContainer = connect(mapStateToProps, {}, mergeProps, {
  forwardRef: true,
})(ConversationCell);
